import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatDate } from '@halo/web-lib';
import {
  KeyRound,
  Search,
  X,
  Loader2,
  Store,
  Building2,
  Lock,
  Clock,
  Copy,
  CheckCircle2,
  AlertTriangle,
  Filter,
  ShieldAlert,
  Activity,
  Timer,
} from 'lucide-react';
import { InfoBadge } from '../components/ui/Tooltip';

// ─── Types ──────────────────────────────────────

interface ApiKeyItem {
  id: string;
  name: string;
  prefix: string;
  scopeType: 'store' | 'agency';
  storeId: number | null;
  storeName: string | null;
  agencyId: number | null;
  agencyName: string | null;
  scopes: string[];
  requestCount: number;
  allowedIps: string[] | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

interface AdminApiKeysResponse {
  keys: ApiKeyItem[];
  total: number;
  limit: number;
  offset: number;
}

const FILTER_OPTIONS = ['All', 'Active', 'Expiring Soon', 'Expired'] as const;
type FilterOption = (typeof FILTER_OPTIONS)[number];

const SORT_OPTIONS = ['Name', 'Created', 'Last Used'] as const;
type SortOption = (typeof SORT_OPTIONS)[number];

// ─── Helpers ────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function timeUntil(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'expired';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

function isExpired(key: ApiKeyItem): boolean {
  return key.expiresAt !== null && new Date(key.expiresAt) < new Date();
}

function isExpiringSoon(key: ApiKeyItem): boolean {
  if (!key.expiresAt) return false;
  const diff = new Date(key.expiresAt).getTime() - Date.now();
  return diff > 0 && diff < 7 * 24 * 60 * 60 * 1000;
}

function isActive(key: ApiKeyItem): boolean {
  return !isExpired(key);
}

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  read: 'Read-only access to store data, dashboards, and analytics.',
  'ingestion:read': 'Read access to ingestion configs, run history, and pipeline status.',
  'ingestion:write': 'Write access to trigger pulls, backfills, and modify ingestion configs.',
  admin: 'Full administrative access including user management and billing.',
};

// ─── Toast ──────────────────────────────────────

function useToast() {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  return { toast, showToast: setToast };
}

// ─── Main Component ─────────────────────────────

export function ApiKeysPage() {
  const queryClient = useQueryClient();
  const { toast, showToast } = useToast();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterOption>('All');
  const [sortBy, setSortBy] = useState<SortOption>('Created');
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);
  const [copiedPrefix, setCopiedPrefix] = useState<string | null>(null);

  const { data: keysData, isLoading } = useQuery({
    queryKey: ['admin', 'api-keys'],
    queryFn: async ({ signal }) => {
      const res = await api.get<AdminApiKeysResponse>('/api/v1/admin/api-keys', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load API keys');
      return res.data;
    },
  });

  const keys = keysData?.keys ?? [];

  const revokeKey = useMutation({
    mutationFn: async (keyId: string) => {
      const res = await api.delete(`/api/v1/admin/api-keys/${keyId}`);
      if (!res.success) throw new Error(res.error ?? 'Failed to revoke API key');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'api-keys'] });
      showToast({ message: 'API key revoked successfully', type: 'success' });
      setConfirmRevokeId(null);
    },
    onError: (err) => {
      showToast({ message: err instanceof Error ? err.message : 'Failed to revoke key', type: 'error' });
      setConfirmRevokeId(null);
    },
  });

  // ─── Summary ──────────────────────────────────

  const summary = useMemo(() => {
    const total = keys.length;
    const active = keys.filter(isActive).length;
    const expired = keys.filter(isExpired).length;
    const expiringSoon = keys.filter(isExpiringSoon).length;
    const uniqueScopes = new Set(keys.flatMap((k) => k.scopes));
    return { total, active, expired, expiringSoon, scopeCount: uniqueScopes.size };
  }, [keys]);

  // ─── Filtered + sorted ────────────────────────

  const filteredKeys = useMemo(() => {
    let list = [...keys];

    if (filter === 'Active') list = list.filter(isActive);
    else if (filter === 'Expired') list = list.filter(isExpired);
    else if (filter === 'Expiring Soon') list = list.filter(isExpiringSoon);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (k) =>
          k.name.toLowerCase().includes(q) ||
          k.prefix.toLowerCase().includes(q) ||
          (k.storeName?.toLowerCase().includes(q) ?? false) ||
          (k.agencyName?.toLowerCase().includes(q) ?? false),
      );
    }

    if (sortBy === 'Name') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'Last Used') {
      list.sort((a, b) => {
        if (!a.lastUsedAt && !b.lastUsedAt) return 0;
        if (!a.lastUsedAt) return 1;
        if (!b.lastUsedAt) return -1;
        return b.lastUsedAt.localeCompare(a.lastUsedAt);
      });
    } else {
      list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }

    return list;
  }, [keys, filter, search, sortBy]);

  const storeKeys = filteredKeys.filter((k) => k.scopeType === 'store');
  const agencyKeys = filteredKeys.filter((k) => k.scopeType === 'agency');

  const handleCopyPrefix = useCallback((prefix: string) => {
    navigator.clipboard.writeText(prefix);
    setCopiedPrefix(prefix);
    setTimeout(() => setCopiedPrefix(null), 2000);
  }, []);

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {toast.message}
          <button onClick={() => showToast(null)} className="ml-2 opacity-70 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-0.5">API Keys</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {summary.total} total key{summary.total !== 1 ? 's' : ''} &middot;{' '}
            {keys.filter((k) => k.scopeType === 'store').length} store-scoped &middot;{' '}
            {keys.filter((k) => k.scopeType === 'agency').length} agency-scoped
            {summary.expiringSoon > 0 && (
              <span className="text-amber-600"> &middot; {summary.expiringSoon} expiring soon</span>
            )}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {([
          { label: 'Total', value: summary.total, color: 'text-gray-900 dark:text-gray-100', icon: KeyRound, filterTo: 'All' as FilterOption },
          { label: 'Active', value: summary.active, color: 'text-green-600 dark:text-green-400', icon: Activity, filterTo: 'Active' as FilterOption },
          { label: 'Expiring', value: summary.expiringSoon, color: summary.expiringSoon > 0 ? 'text-amber-600' : 'text-gray-400 dark:text-gray-500', icon: Timer, filterTo: 'Expiring Soon' as FilterOption },
          { label: 'Expired', value: summary.expired, color: summary.expired > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500', icon: AlertTriangle, filterTo: 'Expired' as FilterOption },
        ]).map((card) => (
          <button
            key={card.label}
            onClick={() => setFilter(filter === card.filterTo && filter !== 'All' ? 'All' : card.filterTo)}
            className={`bg-white dark:bg-gray-900 border rounded-lg px-3 py-2 text-left transition-all hover:shadow-sm ${
              filter === card.filterTo && filter !== 'All' ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <card.icon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
              <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">{card.label}</span>
            </div>
            <div className={`text-lg font-semibold ${card.color}`}>{card.value}</div>
          </button>
        ))}
      </div>

      {/* Search + Filter + Sort */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, prefix, store, or agency..."
            className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Filter className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => setFilter(opt)}
              className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                filter === opt ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-500 dark:text-gray-400 hover:bg-gray-50'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          className="text-xs border border-gray-200 dark:border-gray-800 rounded-lg px-2 py-1.5 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-900"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>Sort: {opt}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      {isLoading ? (
        <Skeleton count={4} />
      ) : keys.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-5 py-12 text-center">
          <KeyRound className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">No API keys found</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 max-w-sm mx-auto">
            API keys allow programmatic access to Halo. Users can create keys from their store settings page or via the MCP service.
          </p>
        </div>
      ) : filteredKeys.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            {search ? `No keys matching "${search}"` : 'No keys match the current filter'}
          </p>
          {(search || filter !== 'All') && (
            <button onClick={() => { setSearch(''); setFilter('All'); }} className="text-xs text-blue-600 dark:text-blue-400 mt-1 hover:underline">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Agency Keys */}
          {agencyKeys.length > 0 && (
            <section className="mb-8">
              <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" /> Agency-Scoped Keys
              </h3>
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100">
                {agencyKeys.map((k) => (
                  <KeyRow
                    key={k.id}
                    apiKey={k}
                    confirmRevokeId={confirmRevokeId}
                    onConfirmRevoke={setConfirmRevokeId}
                    onRevoke={(id) => revokeKey.mutate(id)}
                    isPending={revokeKey.isPending}
                    pendingId={revokeKey.variables}
                    copiedPrefix={copiedPrefix}
                    onCopyPrefix={handleCopyPrefix}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Store Keys */}
          <section>
            <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Store className="w-3.5 h-3.5" /> Store-Scoped Keys
            </h3>
            {storeKeys.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-5 py-4 text-sm text-gray-400 dark:text-gray-500">
                {search.trim() ? 'No matching store keys' : 'No store-scoped keys'}
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100">
                {storeKeys.map((k) => (
                  <KeyRow
                    key={k.id}
                    apiKey={k}
                    confirmRevokeId={confirmRevokeId}
                    onConfirmRevoke={setConfirmRevokeId}
                    onRevoke={(id) => revokeKey.mutate(id)}
                    isPending={revokeKey.isPending}
                    pendingId={revokeKey.variables}
                    copiedPrefix={copiedPrefix}
                    onCopyPrefix={handleCopyPrefix}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Result count */}
      {!isLoading && filteredKeys.length > 0 && (filter !== 'All' || search) && (
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 text-center">
          Showing {filteredKeys.length} of {keys.length} keys
        </p>
      )}
    </div>
  );
}

// ─── Key Row ────────────────────────────────────

interface KeyRowProps {
  apiKey: ApiKeyItem;
  confirmRevokeId: string | null;
  onConfirmRevoke: (id: string | null) => void;
  onRevoke: (id: string) => void;
  isPending: boolean;
  pendingId: string | undefined;
  copiedPrefix: string | null;
  onCopyPrefix: (prefix: string) => void;
}

function KeyRow({
  apiKey,
  confirmRevokeId,
  onConfirmRevoke,
  onRevoke,
  isPending,
  pendingId,
  copiedPrefix,
  onCopyPrefix,
}: KeyRowProps) {
  const isRevoking = isPending && pendingId === apiKey.id;
  const expired = isExpired(apiKey);
  const expiringSoon = isExpiringSoon(apiKey);
  const isConfirming = confirmRevokeId === apiKey.id;

  return (
    <div className={`flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors group ${
      expired ? 'opacity-60' : ''
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
          expired
            ? 'bg-red-50'
            : apiKey.scopeType === 'agency'
              ? 'bg-purple-50'
              : 'bg-blue-50'
        }`}>
          {expired ? (
            <AlertTriangle className="w-4 h-4 text-red-400" />
          ) : apiKey.scopeType === 'agency' ? (
            <Building2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          ) : (
            <KeyRound className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          )}
        </div>
        <div className="min-w-0">
          {/* Name + status badges */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{apiKey.name}</span>

            {/* Status badge */}
            {expired ? (
              <InfoBadge tip="This key has expired and can no longer be used for authentication. It will be rejected by the gateway.">
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/50 font-medium">
                  Expired
                </span>
              </InfoBadge>
            ) : expiringSoon ? (
              <InfoBadge tip={`This key expires in ${timeUntil(apiKey.expiresAt!)}. Consider rotating it soon to avoid service interruption.`}>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600 border border-amber-200 dark:border-amber-900/50 font-medium">
                  Expires in {timeUntil(apiKey.expiresAt!)}
                </span>
              </InfoBadge>
            ) : null}

            {/* IP restriction badge */}
            {apiKey.allowedIps && apiKey.allowedIps.length > 0 && (
              <InfoBadge tip={`Restricted to ${apiKey.allowedIps.length} IP${apiKey.allowedIps.length !== 1 ? 's' : ''}: ${apiKey.allowedIps.join(', ')}. Requests from other IPs will be rejected.`}>
                <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 font-medium">
                  <Lock className="w-2.5 h-2.5" />
                  IP locked
                </span>
              </InfoBadge>
            )}

            {/* Admin scope warning */}
            {apiKey.scopes.includes('admin') && (
              <InfoBadge tip="This key has admin-level access. It can perform all operations including user management and billing. Handle with extreme care.">
                <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/50 font-medium">
                  <ShieldAlert className="w-2.5 h-2.5" />
                  Admin
                </span>
              </InfoBadge>
            )}
          </div>

          {/* Key prefix + metadata */}
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
            {/* Copy-able prefix */}
            <button
              onClick={() => onCopyPrefix(apiKey.prefix)}
              className="inline-flex items-center gap-1 font-mono text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              title="Click to copy key prefix"
            >
              <code>{apiKey.prefix}...</code>
              {copiedPrefix === apiKey.prefix ? (
                <span className="text-[10px] text-green-600 dark:text-green-400 font-sans font-medium">Copied!</span>
              ) : (
                <Copy className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
              )}
            </button>

            <span className="text-gray-300">|</span>

            {/* Scope entity */}
            {apiKey.scopeType === 'agency' ? (
              <span>{apiKey.agencyName ?? `Agency #${apiKey.agencyId}`}</span>
            ) : (
              <span>{apiKey.storeName ?? `Store #${apiKey.storeId}`}</span>
            )}

            <span className="text-gray-300">|</span>

            {/* Request count */}
            <InfoBadge tip={`This key has been used ${apiKey.requestCount.toLocaleString()} time${apiKey.requestCount !== 1 ? 's' : ''} since creation.`}>
              <span>{apiKey.requestCount.toLocaleString()} request{apiKey.requestCount !== 1 ? 's' : ''}</span>
            </InfoBadge>

            <span className="text-gray-300">|</span>

            {/* Created timestamp */}
            <InfoBadge tip={`Created on ${formatDate(apiKey.createdAt)}`}>
              <span className="inline-flex items-center gap-0.5">
                <Clock className="w-3 h-3" />
                Created {timeAgo(apiKey.createdAt)}
              </span>
            </InfoBadge>

            {/* Last used timestamp */}
            {apiKey.lastUsedAt && (
              <>
                <span className="text-gray-300">|</span>
                <InfoBadge tip={`Last used on ${formatDate(apiKey.lastUsedAt)}`}>
                  <span>Last used {timeAgo(apiKey.lastUsedAt)}</span>
                </InfoBadge>
              </>
            )}

            {/* Expiry info (non-warning cases) */}
            {apiKey.expiresAt && !expired && !expiringSoon && (
              <>
                <span className="text-gray-300">|</span>
                <InfoBadge tip={`Expires on ${formatDate(apiKey.expiresAt)}`}>
                  <span>Expires in {timeUntil(apiKey.expiresAt)}</span>
                </InfoBadge>
              </>
            )}
          </div>

          {/* Scope badges */}
          <div className="flex flex-wrap gap-1 mt-1.5">
            {apiKey.scopes.map((s) => (
              <InfoBadge
                key={s}
                tip={SCOPE_DESCRIPTIONS[s] ?? `Grants "${s}" permission to the API key holder.`}
              >
                <span
                  className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${
                    s === 'admin'
                      ? 'bg-red-50 text-red-600'
                      : s.startsWith('ingestion')
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {s}
                </span>
              </InfoBadge>
            ))}
          </div>
        </div>

        {/* Revoke button with inline confirmation */}
        <div className="shrink-0 ml-4">
          {isConfirming ? (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-red-600 dark:text-red-400 font-medium">Revoke?</span>
              <button
                onClick={() => onRevoke(apiKey.id)}
                disabled={isRevoking}
                className="text-[10px] px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isRevoking ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes'}
              </button>
              <button
                onClick={() => onConfirmRevoke(null)}
                className="text-[10px] px-2 py-1 rounded border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors"
              >
                No
              </button>
            </div>
          ) : (
            <button
              onClick={() => onConfirmRevoke(apiKey.id)}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
            >
              Revoke
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Loading Skeleton ───────────────────────────

function Skeleton({ count }: { count: number }) {
  return (
    <div className="space-y-4">
      {/* Summary card skeletons */}
      <div className="grid grid-cols-4 gap-2">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 animate-pulse">
            <div className="flex items-center gap-1.5 mb-1">
              <div className="w-3.5 h-3.5 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded w-12" />
            </div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-8 mt-1" />
          </div>
        ))}
      </div>

      {/* Search bar skeleton */}
      <div className="h-9 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />

      {/* Key row skeletons */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="px-5 py-4 animate-pulse flex items-center gap-3">
            <div className="w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-36" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-14" />
              </div>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 font-mono" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16" />
              </div>
              <div className="flex gap-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-10" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
              </div>
            </div>
            <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded-lg w-16 shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}
